<script>
export default {
    name: 'ReturnScan' 
}
</script>

<script setup>
import url from '@/config/urls';
import { computed, ref, reactive, nextTick, onBeforeUnmount, onMounted } from 'vue';
import { useRouter, onBeforeRouteLeave } from 'vue-router';
import { modalUtil } from '@functions/modalUtil';
import { dateUtil } from '@functions/dateUtil';
import axios from 'axios';
import { apiError } from '@functions/apiError.js';
import { useBarcodeScanner } from '@/services/useBarcodeScanService';
import ApiErrorModal from '@parts/ApiErrorModal.vue';
import DeleteFromBarcodeScanConfirmModal from '@parts/modal/DeleteFromBarcodeScanConfirmModal.vue';
import NoTargetGoodsModal from '@parts/modal/NoTargetGoodsModal.vue';
import AddToReturnBoxConfirmModal from "@parts/modal/AddToReturnBoxConfirmModal.vue";
import NoAddReturnBoxConfirmModal from '@parts/modal/NoAddReturnBoxConfirmModal.vue';
import AddToReturnBoxAppCompleteModal from '@parts/modal/AddToReturnBoxAppCompleteModal.vue';
import ReturnItemsChoiceAppModal from '@parts/modal/ReturnItemsChoiceAppModal.vue';
import DispensingUnitModal from '@parts/modal/DispensingUnitModal.vue';
import TimeLimitOverConfirmModal from '@parts/modal/TimeLimitOverConfirmModal.vue';
import { getBarcodeInfo } from '@functions/barcodeUtil';
import ReturnGoods from '@parts/ReturnGoods.vue';
import { useServiceApplyStore } from '@stores/serviceApply';

// 画面ID
const screenId = ref("ReturnScan");

const props = defineProps({
    topPageUrl: {
        type: String,
        default: url.page.top
    }
    , loginUrl: {
        type: String,
        default: url.login
    }
    , returnBoxCountApiUrl: {
        type: String
        , default: url.api.returnBox.count
    }
    , returnScanSearchApiUrl: {
        type: String,
        default: url.api.returnScanSearch
    }
    , returnBoxAddApiUrl: {
        type: String,
        default: url.api.returnBox.add
    }
    , licenseApiUrl: {
        type: String,
        default: url.api.qrDecoderNeo.license
    }
    , loadingIcon: {
        type: String
        , default: url.static.loadingIcon
    }
    , goodsTypesApiUrl: {
        type: String,
        default: url.api.goodsTypes
    }
    , userDataUrl: {
        type: String,
        default: url.api.userData
    }
    , serviceApplyGetApiUrl: {
        type: String,
        default: url.api.serviceApply.get
    }
});

const user = reactive({
  isPermittedReturnRequest: null,  // null: 未取得, true: 権限あり, false: 権限なし
  barcodeScanApplyPermissionFlg: null
});

const router = useRouter();
const { showModal, hideModal } = modalUtil();
const { formatChitNumber, isTimeLimitOver } = dateUtil();

// ローディングフラグ更新
const handleUpdateLoading = (loading) => {
    isLoading.value = loading;
}

// ローディング
const isLoading = ref(false);

// サービス利用状況
const serviceApplyStore = useServiceApplyStore();
const barcodeScan = computed(() => serviceApplyStore.barcodeScan);

// 返品ボックス行数
const returnBoxTotalRecord = ref(0);
// 数量合計
const returnBoxTotalQuantity = ref(0);
// スキャンデータ
const scannedItem = ref(null);

// 検索結果の返品商品一覧
const returnDataList = ref([]);

// 返品ボックスに追加確認フラグ
const isConfirmedAdd = ref(false);

// 遷移許可フラグ
const allowLeave = ref(false);

// 次ページ遷移フラグ
const nextRoute = ref(null);

// ページ遷移の保留フラグ
const pendingNavigation = ref(null);

// スキャンによる自動登録か返品ボックスに追加ボタン押下による手動登録か
const isManualAdd = ref(false);

// 3カ月経過品のものを返品するかどうか
const isTimeLimitConfirmed = ref(false);

// 商品分類マスタデータ
const goodsTypes = ref([]);

// 削除前数量保持
const previousQuantity = ref(1);

// エラー処理
const handleApiError = (error) => {
	apiError(error, errorMessages);
};

// 商品分類名を取得
const goodsTypeName = (item) => {
    const goodType = goodsTypes.value.find(
        goodsTypeItem => goodsTypeItem.code === item.goodsType
    );
    return goodType ? goodType.name : '';
};

// 返品ボックス内件数取得
const countReturnBoxGoods = async() => {
    try {
        const response = await axios.get(props.returnBoxCountApiUrl);
        returnBoxTotalRecord.value = parseInt(response?.data?.response?.totalNum ?? 0, 10);
        returnBoxTotalQuantity.value = parseInt(response?.data?.response?.totalQuantity ?? 0, 10);
    } catch(error) {
        apiError(error, errorMessages);
    }
}

// スキャン結果をもとに商品検索・分岐処理
const handleScanResult = async(codemark, codedata, { playFoundSound, playNotFoundSound } = {}) => {
    try {
        // ローディング中の場合、中断
        if(isLoading.value) {
            return;
        }
        const barcodeInfo = getBarcodeInfo(codedata);
        // A0: JANコード, B0: 短縮JANコード
        if (codemark === "A0" || codemark === "B0") {
            // 13桁になるように先頭ゼロ埋め
            const janCode = codedata.padStart(13, '0');

            isLoading.value = true;
            const res = await axios.get(props.returnScanSearchApiUrl, {
                params: {
                    janCode: janCode
                    , page: 1
                }
            });
            if (res.data?.response?.goodsList?.length >= 1) {
                await playFoundSound?.();
                returnDataList.value = res.data.response.goodsList;
                showModal('returnItemsChoiceAppModal');
            } else {
                await playNotFoundSound?.();
                showModal('noTargetGoodsModal');
            }
        // GS1コード処理
        } else if(codemark === "R0" || codemark === "V0") {
            const salePackagingUnitCode = barcodeInfo.gtin;
            const lot = barcodeInfo.lot;

            // 調剤包装単位コード(先頭が0)
            if (salePackagingUnitCode?.startsWith("0")) {
                await playNotFoundSound?.();
                showModal('dispensingUnitModal');

            // 販売包装単位(先頭1)
            } else if(salePackagingUnitCode?.startsWith("1")) {
                isLoading.value = true;
                const res = await axios.get(props.returnScanSearchApiUrl, {
                    params: {
                        salePackagingUnitCode: salePackagingUnitCode
                        , lot: lot
                    }
                });

                if (res.data?.response?.goodsList?.length >= 1) {
                    await playFoundSound?.();
                    returnDataList.value = res.data.response.goodsList;
                    showModal('returnItemsChoiceAppModal');
                } else {
                    await playNotFoundSound?.();
                    showModal('noTargetGoodsModal');
                }
            } else {
                // 返品では元梱包包装単位コードは使わない
                await playNotFoundSound?.();
                showModal('noTargetGoodsModal');
            }
        } else {
            // バーコードが読み取れたが商品がない
            await playNotFoundSound?.();
            showModal('noTargetGoodsModal');
        }
    } catch(error) {
        apiError(error, errorMessages);
    } finally {
        isLoading.value = false;
    }
}

const formatDate = (y, m, d) => {
    const mm = String(m).padStart(2, '0');
    return `${y}.${mm}`;
};

// 返品ボックス追加前確認後、返品ボックスへ追加
const tryAddToReturnBox = async(manual = false) => {
    if (!scannedItem.value) return false;
    const salesDetailsLotId = scannedItem.value.salesDetailsLotId;

    isManualAdd.value = manual;

    handleUpdateLoading(true);
    try {
        const resGoodsCount = await axios.get(props.returnBoxCountApiUrl, {
            params: {
                salesDetailsLotIdList: [salesDetailsLotId]
            }
        });
        if (isTimeLimitOver(scannedItem.value.chitDate)) {
            handleUpdateLoading(false);
            isTimeLimitConfirmed.value = false;
            showModal('timeLimitOverConfirmModal');
            const result = await waitModalClose('timeLimitOverConfirmModal', () => isTimeLimitConfirmed.value);
            if (!result) {
                return false;
            }
        }
        if (resGoodsCount.data.response.totalNum === 0) {
            await addToReturnBox();
            return true;
        } else {
            handleUpdateLoading(false);
            isConfirmedAdd.value = false;
            showModal('addToReturnBoxConfirmModal');
            return await waitModalClose('addToReturnBoxConfirmModal', () => isConfirmedAdd.value);
        }
    } catch(error) {
        apiError(error, errorMessages);
    } finally {
        handleUpdateLoading(false);
    }
    
}

// 商品を返品ボックスに追加するAPI呼び出し
const addToReturnBox = async()=> {
    if (!scannedItem.value) return;

    const salesDetailsLotId = scannedItem.value.salesDetailsLotId;
    const quantity = scannedItem.value.quantity;

    handleUpdateLoading(true);
    try {
        await axios.post(props.returnBoxAddApiUrl, {
            goodsList: [
                {
                salesDetailsLotId
                , quantity
                }
            ]
        });
        // 手動追加時はここでクリア
        if (isManualAdd.value) {
            clearScannedData();
            isManualAdd.value = false;
        }
        await countReturnBoxGoods();
        // 完了モーダル表示
        showModal('addToReturnBoxAppCompleteModal');
    } catch(error) {
        apiError(error, errorMessages);
    } finally {
        handleUpdateLoading(false);
    };
};

// 返品商品一覧を検索して選択押下時に、スキャン商品として設定する
const handleSelectReturnItem = async (returnItem) => {

    // 前回スキャン商品を返品ボックスへ
    if (scannedItem.value) {
        const result = await tryAddToReturnBox();

        if(!result) {
            return false;
        }
    }
    scannedItem.value = {
        salesDetailsLotId: returnItem.salesDetailsLotId
        , chitNumber: returnItem.chitNumber
        , chitDate: returnItem.chitDate
        , goodsType: returnItem.goodsType
        , janCode: returnItem.janCode
        , makerName: returnItem.makerName
        , formalGoodsName: returnItem.formalGoodsName
        , lot: returnItem.lot
        , expirationDate1: returnItem.expirationDate1
        , deliveryQuantity: returnItem.deliveryQuantity
        , quantity: 1
        , salePackagingUnitCode: returnItem.salePackagingUnitCode
        , canReturn: returnItem.canReturn
        , returnProhibitedReason: returnItem.returnProhibitedReason
        , returnProhibitedReasonCode: returnItem.returnProhibitedReasonCode
    }
}

const handleQuantityChanged = (salesDetailsLotId, quantity) => {
    if (scannedItem.value && scannedItem.value.salesDetailsLotId === salesDetailsLotId) {
        scannedItem.value.quantity = quantity;
    }
};

// 数量バリデーションエラー
const handleValidationError = (salesDetailsLotId, hasError, quantityData) => {
    // エラーなしなら何もしない
    if (!hasError || !quantityData) {
        return;
    }
    previousQuantity.value = quantityData.previousQuantity;
    if (Number(quantityData.currentQuantity) <= 0) {
        showModal('deleteFromBarcodeScanConfirmModal');
    }
}

// スキャン商品削除キャンセル
const cancelDeleteFromBarcodeScan = () => {
    if (!scannedItem.value) return;

    scannedItem.value.quantity = previousQuantity.value
    scannedItem.value = { ...scannedItem.value };
    hideModal('deleteFromBarcodeScanConfirmModal');
}

// スキャン商品 削除
const deleteFromBarcodeScan = () => {
    if (!scannedItem.value) return;

    // 商品を削除
    scannedItem.value = null;
}

// 伝票日付3ヵ月経過警告モーダル「はい」押下時処理
const handleTimeLimitOk = () => {
    isTimeLimitConfirmed.value = true;

    hideModal('timeLimitOverConfirmModal')
}

// 返品ボックスに追加する場合はモーダルを閉じ、返品ボックスに追加
const handleConfirmAddToReturnBox = async() => {
    isConfirmedAdd.value = true;

    hideModal('addToReturnBoxConfirmModal');
    await addToReturnBox();
}

// モーダルで選択したボタンによって制御を分岐
const waitModalClose = (modalId, getResult) => {
    return new Promise((resolve) => {
        const modalEl = document.getElementById(modalId);

        if(!modalEl) {
            resolve(false);
            return;
        }

        const modalHandler = () => {
            modalEl.removeEventListener('hidden.bs.modal', modalHandler);

            resolve(getResult());
        };
        modalEl.addEventListener('hidden.bs.modal', modalHandler);
    })
}

// 完了画面を閉じたらスキャン商品をクリアし、ページ遷移先に移動する
const handleCompleteModalHidden = () => {
    if (pendingNavigation.value === 'back') {
        pendingNavigation.value = null;
        router.back();
        return;
    }
    if (pendingNavigation.value) {
        if (pendingNavigation.value.fullPath) {
            router.push(pendingNavigation.value.fullPath);
        } else if (pendingNavigation.value.path) {
            router.push(pendingNavigation.value.path);
        } else {
            router.back();
        }
        pendingNavigation.value = null;
    }
};

// スキャンデータをクリア
const clearScannedData = () => {
    scannedItem.value = null;
};

// 返品ボックスに追加して画面遷移
const handleAddAndLeave = async() => {
    allowLeave.value = true;

    pendingNavigation.value = nextRoute.value || { path: props.topPageUrl };

    const result = await tryAddToReturnBox();

    if (!result) {
        allowLeave.value = false;
        pendingNavigation.value = null;
        return;
    }
};

// 返品ボックスに追加せず画面遷移
const handleLeaveWithoutAdd = async() => {
    allowLeave.value = true;
    await stopCameraPreview();

    if (nextRoute.value?.fullPath) {
        router.push(nextRoute.value.fullPath);
    } else if (nextRoute.value?.path) {
        router.push(nextRoute.value.path);
    } else {
        router.back();
    }
}

// 戻るボタン押下時の処理
// 未追加商品があれば確認モーダル表示
const handleBeforeLeave = async() => {
    if (!scannedItem.value) {
        await stopCameraPreview();
        router.back();
        return;
    }

    nextRoute.value = 'back';
    showModal('noAddReturnBoxConfirmModal');
};

// 画面離脱ガード
// 未追加の商品がある場合は遷移を止める
onBeforeRouteLeave(async(to) => {
    // すでに許可済みもしくはスキャン商品がないなら通す
    if (allowLeave.value || !scannedItem.value) {
        await stopCameraPreview();
        return true;
    }

    // 遷移先を保持して止める
    nextRoute.value = to;
    showModal('noAddReturnBoxConfirmModal');
    return false;
});

// 商品分類マスタデータ取得処理
const getGoodsTypes = async() => {
    try {
        const response = await axios.get(props.goodsTypesApiUrl);
        goodsTypes.value = response?.data?.response?.goodsTypes || [];
    } catch(error) {
        apiError(error, errorMessages);
    }
}

// 共通スキャンサービス
const {
    cameraAreaRef
    , videoRef
    , canvasRef
    , scanError
    , isReading
    , isScanSuccessHighlight
    , errorMessages
    , fetchLicense
    , startCameraPreview
    , stopCameraPreview
    , stopRotationWatcher
    , startScan
} = useBarcodeScanner({
    loginUrl: props.loginUrl
    , licenseApiUrl: props.licenseApiUrl
    , onScanResult: handleScanResult,
})

// ユーザー情報取得APIの呼び出し
const getUserInfo = async () => {
  try {
    const response = await axios.get(props.userDataUrl);
    user.isPermittedReturnRequest = response.data.response.isPermittedReturnRequest;
    user.barcodeScanApplyPermissionFlg = response.data.response.barcodeScanApplyPermissionFlg;
  } catch (error) {
    apiError(error, errorMessages);
  }
}

onMounted(async() => {
    await getUserInfo();
    if (!user.isPermittedReturnRequest) {
        return;
    }

    await getGoodsTypes();
    await countReturnBoxGoods();
    await fetchLicense();
    await startCameraPreview();
    await serviceApplyStore.fetchServiceApply(props.serviceApplyGetApiUrl);

    if (!barcodeScan?.value?.isAvailable || user.barcodeScanApplyPermissionFlg !== 1) {
        router.push(props.topPageUrl);
    }
});

onBeforeUnmount(async() => {
    stopRotationWatcher();
    await stopCameraPreview();
});

</script>

<template>
    <!-- スキャン画面 -->
    <div class="com_app_bscan_wrap1" v-if="user.isPermittedReturnRequest">
        <div class="com_app_bscan_nav1">
            <div class="title1">
                返品スキャン
            </div>
            <a href="#" class="prev1 l" @click.prevent="handleBeforeLeave"></a>
            <router-link :to="url.page.returnBox" class="txt1 r">返品ボックスへ</router-link>
        </div>

        <div class="com_app_bscan_text1">
            <p class="com_fs16 text-center">
                商品のバーコードをスキャンしてください
            </p>
        </div>

        <!-- カメラエリア -->
        <div
            ref="cameraAreaRef"
            class="com_app_bscan_area1"
            :class="{ 'scan-success-highlight': isScanSuccessHighlight }"
        >
            <video ref="videoRef" class="preview-video" autoplay playsinline muted></video>
            <span v-if="isReading" class="cross1"></span>
            <div v-if="!isReading" class="scan-overlay">
                <p>読み取るボタンで<br>開始します</p>
            </div>
        </div>
        <canvas ref="canvasRef" width="1920" height="1080" style="display: none;"></canvas>
        <p v-if="scanError" class="com_fs12 error" style="white-space: pre-wrap; word-break: break-word; text-align: left;">{{ scanError }}</p>

        <!-- 集計 -->
        <div class="com_app_bscan_data1">
            <div>返品ボックス行数：{{ returnBoxTotalRecord }}</div>
            <div>数量合計：{{ returnBoxTotalQuantity }}</div>
        </div>

        <!-- スキャン結果表示 -->
        <div class="goods-scroll-area">
            <ReturnGoods
                v-if="scannedItem"
                :screenId = "screenId"
                :goods = "scannedItem"
                :goodsTypes = "goodsTypes"
                :index = "0"
                @updateLoading="handleUpdateLoading"
                @apiError="handleApiError"
                @validationError="handleValidationError"
                @quantityChanged="handleQuantityChanged"
            />
        </div>
        

        <!-- ボタン -->
        <div class="com_app_bscan_buttons1">
            <div>
                <button type="button" class="btn_scan3" @click="startScan">
                    <img src="/front/assets/common/img/btn_scan5.png" alt="読取ボタン">
                </button>

                <a href="#" class="btn1 w1" :class="{ disabled: !scannedItem}" @click.prevent="tryAddToReturnBox(true)">
                    返品ボックスに追加<img src="/front/assets/common/img/icon_return_box3.png">
                </a>
            </div>
        </div>
    </div>

    <Transition>
        <div class="loading-animation" v-show="isLoading">
            <div class="show-icon-box">
                <img :src="props.loadingIcon" />
            </div>
        </div>
    </Transition>

    <!-- APIエラーモーダル -->
    <ApiErrorModal :messages="errorMessages" />

    <!-- スキャン商品確認モーダル -->
    <DeleteFromBarcodeScanConfirmModal 
        @deleteFromBarcodeScan="deleteFromBarcodeScan"
        @cancelDelete="cancelDeleteFromBarcodeScan"
    />

    <!-- 対象商品なしモーダル -->
    <NoTargetGoodsModal />

    <!-- 調剤包装単位コード読み取り不可モーダル -->
    <DispensingUnitModal />

    <!-- 返品対象商品一覧選択モーダル -->
    <ReturnItemsChoiceAppModal
        :returnItems="returnDataList"
        :goods-types="goodsTypes"
        @selectReturnItem="handleSelectReturnItem"
    />

    <!-- 返品ボックスに追加確認モーダル -->
    <AddToReturnBoxConfirmModal @addToReturnBox="handleConfirmAddToReturnBox" />

    <!-- 返品ボックスに入れていない確認モーダル -->
    <NoAddReturnBoxConfirmModal
        @addToReturnBox="handleAddAndLeave"
        @backPrev="handleLeaveWithoutAdd" />

    <AddToReturnBoxAppCompleteModal @hidden="handleCompleteModalHidden" />

    <!-- 伝票日付3ヵ月経過警告モーダル -->
    <TimeLimitOverConfirmModal @timeLimitOverConfirmed="handleTimeLimitOk" />

</template>

<style scoped>

.preview-video {
    position: absolute;
    inset: 0;
    height: 100%;
    width: 100%;
    object-fit: cover;
}

.scan-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(80, 80, 80, 0.75);
    z-index: 2;
}

.scan-overlay p {
    margin: 0;
    color: #fff;
    font-size: 4.8vw;
    font-weight: 700;
    line-height: 1.5;
    text-align: center;
}

:global(body.scan-camera-preview-active),
:global(body.scan-camera-preview-active html),
:global(body.scan-camera-preview-active #app),
:global(body.scan-camera-preview-active #wrapper1){
    background: transparent !important;
    overflow: hidden;
}

:global(body.scan-camera-preview-active .com_app_bscan_nav1),
:global(body.scan-camera-preview-active .com_app_bscan_text1),
:global(body.scan-camera-preview-active .goods-scroll-area),
:global(body.scan-camera-preview-active .com_app_bscan_buttons1 > div) {
    background: #fff !important;
    margin: 0;
}

:global(body.scan-camera-preview-active .com_app_bscan_area1 video) {
    opacity: 0;
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
}
</style>